// hooks/useFestivos.ts
import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

export interface Festivo {
  id_festivo: string;
  id_empresa: string;
  fecha: string;
  motivo: string;
  fecha_creacion?: string;
  fecha_edicion?: string;
}

export function useFestivos(id_empresa: string) {
  // ============================================================
  // ESTADOS
  // ============================================================
  const [festivos, setFestivos] = useState<Festivo[]>([]);
  const [loading, setLoading] = useState(false);

  const currentYear = new Date().getFullYear();
  const [añoSeleccionado, setAñoSeleccionado] = useState(currentYear);

  // ============================================================
  // CARGAR FESTIVOS
  // ============================================================
  const fetchFestivos = useCallback(
    async (year = añoSeleccionado) => {
      if (!id_empresa) return;

      setLoading(true);

      const { data, error } = await supabase
        .from('vista_dias_festivos')
        .select(
          'id_festivo, id_empresa, fecha, motivo, fecha_creacion, fecha_edicion'
        )
        .eq('id_empresa', id_empresa)
        .gte('fecha', `${year}-01-01`)
        .lte('fecha', `${year}-12-31`)
        .order('fecha', { ascending: true });

      setLoading(false);

      if (error) {
        Sentry.captureException(error, { extra: { id_empresa, year } }); // 👈
        toast.error('Error al cargar los festivos.');
        return;
      }

      setFestivos(data || []);
    },
    [id_empresa, añoSeleccionado]
  );

  // ============================================================
  // CREAR FESTIVO
  // ============================================================
  const crearFestivo = async ({
    fecha,
    motivo,
    usuario,
  }: {
    fecha: string;
    motivo: string;
    usuario: string | null;
  }) => {
    if (!fecha) {
      toast.error('Selecciona una fecha.');
      return { ok: false };
    }

    const { data: existente, error: errDup } = await supabase
      .from('festivos')
      .select('id_festivo')
      .eq('id_empresa', id_empresa)
      .eq('fecha', fecha)
      .maybeSingle();

    if (errDup) {
      Sentry.captureException(errDup, { extra: { id_empresa, fecha } }); // 👈
      toast.error('Error al verificar duplicados.');
      return { ok: false };
    }

    if (existente) {
      toast.error('Ya existe un festivo en esa fecha.');
      return { ok: false };
    }

    setLoading(true);

    const { error } = await supabase.from('festivos').insert({
      id_empresa,
      fecha,
      motivo,
      usuario_creacion: usuario,
    });

    setLoading(false);

    if (error) {
      Sentry.captureException(error, { extra: { id_empresa, fecha } }); // 👈
      toast.error('Error al guardar festivo.');
      return { ok: false };
    }

    toast.success('Festivo creado correctamente.');
    await fetchFestivos();
    return { ok: true };
  };

  // ============================================================
  // ELIMINAR FESTIVO
  // ============================================================
  const eliminarFestivo = async (id_festivo: string) => {
    setLoading(true);

    const { error } = await supabase
      .from('festivos')
      .delete()
      .eq('id_festivo', id_festivo);

    setLoading(false);

    if (error) {
      Sentry.captureException(error, { extra: { id_empresa, id_festivo } }); // 👈
      toast.error('Error al eliminar festivo.');
      return { ok: false };
    }

    toast.success('Festivo eliminado.');
    await fetchFestivos();
    return { ok: true };
  };

  // ============================================================
  // CAMBIAR AÑO (FILTRO)
  // ============================================================
  const cambiarAño = async (nuevoAño: number) => {
    setAñoSeleccionado(nuevoAño);
    await fetchFestivos(nuevoAño);
  };

  // ============================================================
  // COPIAR FESTIVOS DE UN AÑO A OTRO
  // ============================================================
  const copiarFestivos = async ({
    añoOrigen,
    añoDestino,
  }: {
    añoOrigen: number;
    añoDestino: number;
  }) => {
    if (!id_empresa) return { ok: false };

    if (añoOrigen === añoDestino) {
      toast.error('El año origen y destino no pueden ser el mismo.');
      return { ok: false };
    }

    setLoading(true);

    // 1) Traer festivos del año origen
    const { data: origen, error: errOrigen } = await supabase
      .from('festivos')
      .select('fecha, motivo')
      .eq('id_empresa', id_empresa)
      .gte('fecha', `${añoOrigen}-01-01`)
      .lte('fecha', `${añoOrigen}-12-31`)
      .order('fecha', { ascending: true });

    if (errOrigen) {
      Sentry.captureException(errOrigen, { extra: { id_empresa, añoOrigen } }); // 👈
      setLoading(false);
      toast.error('Error al cargar festivos del año origen.');
      return { ok: false };
    }

    if (!origen || origen.length === 0) {
      setLoading(false);
      toast.error(`No hay festivos en el año ${añoOrigen}.`);
      return { ok: false };
    }

    // 2) Construir nuevas fechas para el año destino
    const candidatos = origen.map((f) => ({
      id_empresa,
      fecha: `${añoDestino}-${f.fecha.slice(5)}`,
      motivo: f.motivo,
      usuario_creacion: localStorage.getItem('id_usuario'),
    }));

    // 3) Traer existentes del año destino para evitar duplicados
    const { data: yaExisten, error: errExisten } = await supabase
      .from('festivos')
      .select('fecha')
      .eq('id_empresa', id_empresa)
      .gte('fecha', `${añoDestino}-01-01`)
      .lte('fecha', `${añoDestino}-12-31`);

    if (errExisten) {
      Sentry.captureException(errExisten, {
        extra: { id_empresa, añoDestino },
      }); // 👈
      setLoading(false);
      toast.error('Error al verificar festivos existentes del año destino.');
      return { ok: false };
    }

    const setExistentes = new Set((yaExisten ?? []).map((x) => x.fecha));
    const nuevos = candidatos.filter((c) => !setExistentes.has(c.fecha));

    if (nuevos.length === 0) {
      setLoading(false);
      toast.error(`Ya existen todos los festivos en el año ${añoDestino}.`);
      return { ok: false };
    }

    const { error: errInsert } = await supabase.from('festivos').insert(nuevos);

    setLoading(false);

    if (errInsert) {
      Sentry.captureException(errInsert, {
        extra: { id_empresa, añoDestino, count: nuevos.length },
      }); // 👈
      toast.error('Error al copiar festivos.');
      return { ok: false };
    }

    toast.success(`Festivos copiados a ${añoDestino}: ${nuevos.length}.`);
    await cambiarAño(añoDestino);
    return { ok: true };
  };

  // ============================================================
  // API DEL HOOK
  // ============================================================
  return {
    festivos,
    loading,
    añoSeleccionado,
    cambiarAño,
    crearFestivo,
    eliminarFestivo,
    copiarFestivos,
    refrescar: fetchFestivos,
  };
}
